import { useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  getListOrganizationsQueryKey,
  useListOrganizations,
} from "~/api/organizations/organizations";
import { Button } from "~/components/ui/button";

interface MonthSelectorProps {
  onMonthChange: (from: string) => void;
}

export const MonthSelector: FC<MonthSelectorProps> = ({ onMonthChange }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [organizationCreationDate, setOrganizationCreationDate] =
    useState<Date | null>(null);
  const debouncedDate = useDebounce(currentDate, 300);

  const { data, error } = useListOrganizations();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (data && data.length > 0 && data[0].createdAt) {
      setOrganizationCreationDate(new Date(data[0].createdAt));
    } else if (error) {
      if (error.code === 401) {
        queryClient.invalidateQueries({
          queryKey: getListOrganizationsQueryKey(),
        });
      }
    }
  }, [data, error]);

  const updateMonth = useCallback((direction: "prev" | "next") => {
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      if (direction === "prev") {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
  }, []);

  const isCurrentMonth = useMemo(() => {
    const now = new Date();
    return (
      currentDate.getMonth() === now.getMonth() &&
      currentDate.getFullYear() === now.getFullYear()
    );
  }, [currentDate]);

  const isOrganizationCreationMonth = useMemo(() => {
    if (!organizationCreationDate) return false;
    return (
      currentDate.getMonth() === organizationCreationDate.getMonth() &&
      currentDate.getFullYear() === organizationCreationDate.getFullYear()
    );
  }, [currentDate, organizationCreationDate]);

  useEffect(() => {
    const from = new Date(
      debouncedDate.getFullYear(),
      debouncedDate.getMonth(),
      1
    ).toISOString();
    onMonthChange(from);
  }, [debouncedDate]);

  const formatMonth = (date: Date) => {
    return date.toLocaleString("default", { month: "short" }).slice(0, 3);
  };

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="icon"
        className="p-2 h-10"
        disabled={isOrganizationCreationMonth}
        onClick={() => updateMonth("prev")}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <span className="text-sm font-medium py-2 px-4 rounded-md border h-10">
        {`${formatMonth(currentDate)} ${currentDate.getFullYear()}`}
      </span>
      <Button
        variant="outline"
        size="icon"
        className="p-2 h-10"
        disabled={isCurrentMonth}
        onClick={() => updateMonth("next")}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
};
